import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { PolicyService, ContractDefinitionService } from "../../../management-api-client";
import { first, map, catchError } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { NewPolicyDialogComponent } from "../new-policy-dialog/new-policy-dialog.component";
import { ConfirmationDialogComponent, ConfirmDialogModel } from "../confirmation-dialog/confirmation-dialog.component";
import { PolicyDefinition, PolicyDefinitionInput } from "../../../management-api-client/model";
import { NotificationService, SorterService, UtilService } from '../../services';
import { PageEvent } from '@angular/material/paginator';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedStateService } from 'src/modules/app/auth/unauthorized-state.service';

@Component({
  selector: 'app-policy-view',
  templateUrl: './policy-view.component.html',
  styleUrls: ['./policy-view.component.scss']
})
export class PolicyViewComponent implements OnInit, OnDestroy {
  paginationState = {
    filteredList: [] as any[],
    pagedList: [] as any[],
    pageIndex: 0,
    pageSize: 20
  };
  totalPolicies = 0;
  searchText: string = '';
  isUnauthorized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private policyService: PolicyService,
    private contractDefinitionService: ContractDefinitionService,
    private notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly sorterService: SorterService,
    private readonly cdref: ChangeDetectorRef,
    private readonly unauthorizedState: UnauthorizedStateService,
    public readonly utilService: UtilService,
  ) {}

  loadPolicies() {
    const { pageIndex, pageSize } = this.paginationState;
    this.policyService.queryAllPolicies({
      limit: pageSize + 1,
      offset: pageIndex * pageSize,
    }).subscribe({
      next: (policies: any[]) => {
        const hasMore = policies.length > pageSize;
        const page = hasMore ? policies.slice(0, pageSize) : policies;

        this.paginationState.pagedList = page.sort((a: any, b: any) =>
          this.sorterService.naturalSort(a.id, b.id)
        );
        this.paginationState.filteredList = this.paginationState.pagedList;
        this.totalPolicies = hasMore
          ? (pageIndex + 2) * pageSize
          : pageIndex * pageSize + page.length;
      },
      error: (err: HttpErrorResponse) => {
        if (this.unauthorizedState.isUnauthorized('management')) {
          return;
        }
        this.showError(err as any, 'Failed to load policies');
      }
    });
  }

  ngOnInit(): void {
    this.unauthorizedState
      .isUnauthorized$('management')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUnauthorized) => {
        this.isUnauthorized = isUnauthorized;
        if (isUnauthorized) {
          this.paginationState.filteredList = [];
          this.paginationState.pagedList = [];
        }
      });

    this.loadPolicies();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch() {
    this.paginationState.pageIndex = 0;
    this.loadPolicies();
  }

  onPageChange(event: PageEvent) {
    this.paginationState.pageIndex = event.pageIndex;
    this.paginationState.pageSize = event.pageSize;
    this.loadPolicies();
  }

  onCreate() {
    const dialogRef = this.dialog.open(NewPolicyDialogComponent);
    dialogRef.afterClosed().pipe(first()).subscribe({ next: (newPolicyDefinition: PolicyDefinitionInput) => {
        if (newPolicyDefinition) {
          this.policyService.createPolicy(newPolicyDefinition).subscribe({
            next: () => {
              this.notificationService.showInfo("Policy created successfully");
              this.loadPolicies();
            },
            error: (error: any) => this.showError(error, this.utilService.getApiErrorMessage(error) ?? "An error occurred while creating the policy.")
          });
        }
      }
    });
  }

  onEdit(policy: any) {
    const policyId = policy.id ?? policy['@id'];
    const dialogRef = this.dialog.open(NewPolicyDialogComponent, { data: policy });
    dialogRef.afterClosed().pipe(first()).subscribe({ next: (editedPolicyDefinition: PolicyDefinitionInput) => {
        if (editedPolicyDefinition) {
          this.policyService.updatePolicy(policyId, editedPolicyDefinition).subscribe({
            next: () => {
              this.notificationService.showInfo("Policy updated successfully");
              this.loadPolicies();
            },
            error: (error: any) => this.showError(error, this.utilService.getApiErrorMessage(error) ?? "An error occurred while updating the policy.")
          });
        }
      }
    });
  }

  delete(policy: any) {
    const policyId = policy.id ?? policy['@id'];
    this.countReferencingContractDefinitions(policyId).subscribe(referenceCount => {
      const warning = referenceCount > 0
        ? `This policy is used by ${referenceCount} contract definition(s). The connector will refuse to delete a policy that is still referenced.`
        : undefined;
      const dialogData = ConfirmDialogModel.forDelete("policy", policyId, warning);
      const ref = this.dialog.open(ConfirmationDialogComponent, {data: dialogData});

      ref.afterClosed().subscribe({
        next: (res: any) => {
          if (res) {
            this.policyService.deletePolicy(policyId).subscribe({
              next: () => {
                this.notificationService.showInfo("Policy deleted successfully");
                this.loadPolicies();
              },
              error: (error: any) => this.showError(error, this.getDeleteErrorMessage(error))
            });
          }
        }
      });
    });
  }

  private countReferencingContractDefinitions(policyId: string): Observable<number> {
    const queryByField = (field: string) => this.contractDefinitionService.queryAllContractDefinitions({
      filterExpression: [{ operandLeft: field, operator: '=', operandRight: policyId }]
    }).pipe(catchError(() => of([] as any[])));

    return forkJoin([queryByField('accessPolicyId'), queryByField('contractPolicyId')]).pipe(
      map(([byAccess, byContract]) => new Set([...byAccess, ...byContract].map((cd: any) => cd.id)).size),
      // the check is best-effort; do not block the action if the query fails
      catchError(() => of(0))
    );
  }

  private getDeleteErrorMessage(error: any): string {
    const detail = this.utilService.getApiErrorMessage(error);
    if (detail) return detail;
    return error?.status === 409
      ? "The policy cannot be deleted because it is referenced by an existing contract definition."
      : "An error occurred while deleting the policy.";
  }

  private showError(error: Error, errorMessage: string) {
    console.error(error);
    this.notificationService.showError(errorMessage);
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }
}
